import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDownRight, ArrowUpRight, RefreshCw } from 'lucide-react';
import { Heading } from '../../layout';
import { useUIColors } from '../../../hooks/useTheme';
import {
  getFinancialStatements,
  getFinancialStatementsComparative,
} from '../../../services/treasuryAPI';
import {
  PERIOD_OPTIONS,
  MONTH_OPTIONS,
  getAvailableYearsFromDates,
  resolvePeriodSelection,
  periodLabelStyle,
  periodSelectStyle,
} from '../../placeholders/periodSummaryUtils';

const money = (value) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const moneyCompact = (value) => {
  const n = Number(value || 0);
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${n < 0 ? '-' : ''}$${(abs / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${n < 0 ? '-' : ''}$${Math.round(abs / 1_000)}k`;
  return money(n);
};

const toIsoDate = (value) => {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return '';
  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TABS = [
  { key: 'income', label: 'Estado de Resultados' },
  { key: 'balance', label: 'Balance General' },
  { key: 'cashflow', label: 'Flujo de Caja' },
];

const INCOME_VIEW_MODES = [
  { key: 'comparative', label: 'Comparativa por periodo' },
  { key: 'detail', label: 'Detalle del periodo' },
];

const INCOME_ROW_KEYS = new Set(['revenues', 'costs', 'gross_profit', 'expenses', 'net_income']);

const DeltaBadge = ({ delta, ui }) => {
  if (delta == null) {
    return <span style={{ fontSize: 10, color: ui.muted }}>—</span>;
  }
  const n = Number(delta || 0);
  const positive = n > 0;
  const negative = n < 0;
  const color = positive ? '#166534' : negative ? ui.danger || '#b91c1c' : ui.muted;
  const Icon = positive ? ArrowUpRight : negative ? ArrowDownRight : null;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
        gap: 2,
        fontSize: 10,
        fontWeight: 700,
        color,
      }}
      title={`Salto vs mes anterior: ${money(n)}`}
    >
      {Icon ? <Icon size={11} /> : null}
      {n > 0 ? '+' : ''}
      {moneyCompact(n)}
    </span>
  );
};

const StatementSectionTable = ({ ui, title, section, emptyLabel = 'Sin movimientos' }) => {
  const groups = Array.isArray(section?.groups) ? section.groups : [];
  return (
    <div
      style={{
        border: `1px solid ${ui.border}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: ui.page || ui.panel,
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${ui.border}`,
          background: ui.header,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>{title}</h4>
        <span style={{ fontSize: 13, fontWeight: 700, color: ui.text }}>{money(section?.total)}</span>
      </div>
      {!groups.length ? (
        <p style={{ margin: 0, padding: 16, color: ui.muted, fontSize: 13 }}>{emptyLabel}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 520 }}>
            <thead>
              <tr style={{ background: ui.header, borderBottom: `1px solid ${ui.border}` }}>
                {['Cuenta', 'Debe', 'Haber', 'Saldo'].map((label) => (
                  <th
                    key={label}
                    style={{
                      padding: '8px 12px',
                      textAlign: label === 'Cuenta' ? 'left' : 'right',
                      fontSize: 11,
                      fontWeight: 700,
                      color: ui.muted,
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <React.Fragment key={group.name}>
                  <tr style={{ background: ui.rowHover || ui.header }}>
                    <td
                      colSpan={4}
                      style={{
                        padding: '8px 12px',
                        fontSize: 12,
                        fontWeight: 700,
                        color: ui.text,
                        borderBottom: `1px solid ${ui.border}`,
                      }}
                    >
                      {group.name}
                    </td>
                  </tr>
                  {(group.accounts || []).map((account) => (
                    <tr
                      key={`${account.account_code}-${account.account_label}`}
                      style={{ borderBottom: `1px solid ${ui.border}` }}
                    >
                      <td style={{ padding: '8px 12px' }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: ui.text }}>
                          {account.account_label}
                        </div>
                        <div style={{ fontSize: 11, color: ui.muted }}>{account.account_code}</div>
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: ui.text }}>
                        {account.debe ? money(account.debe) : '—'}
                      </td>
                      <td style={{ padding: '8px 12px', textAlign: 'right', fontSize: 12, color: ui.text }}>
                        {account.haber ? money(account.haber) : '—'}
                      </td>
                      <td
                        style={{
                          padding: '8px 12px',
                          textAlign: 'right',
                          fontSize: 12,
                          fontWeight: 700,
                          color: ui.text,
                        }}
                      >
                        {money(account.balance)}
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
              <tr>
                <td colSpan={3} style={{ padding: '10px 12px', fontWeight: 700, fontSize: 13, color: ui.text }}>
                  Total {title}
                </td>
                <td
                  style={{
                    padding: '10px 12px',
                    textAlign: 'right',
                    fontWeight: 700,
                    fontSize: 13,
                    color: ui.text,
                  }}
                >
                  {money(section?.total)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const CashFlowBlock = ({ ui, title, section }) => {
  const lines = Array.isArray(section?.lines) ? section.lines : [];
  return (
    <div
      style={{
        border: `1px solid ${ui.border}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: ui.page || ui.panel,
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${ui.border}`,
          background: ui.header,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>{title}</h4>
        <span style={{ fontSize: 13, fontWeight: 700, color: ui.text }}>{money(section?.total)}</span>
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <tbody>
          {lines.map((line) => (
            <tr key={line.key} style={{ borderBottom: `1px solid ${ui.border}` }}>
              <td style={{ padding: '10px 12px', fontSize: 13, color: ui.text }}>{line.label}</td>
              <td
                style={{
                  padding: '10px 12px',
                  textAlign: 'right',
                  fontSize: 13,
                  fontWeight: 650,
                  color: Number(line.amount) < 0 ? ui.danger || '#b91c1c' : ui.text,
                }}
              >
                {money(line.amount)}
              </td>
            </tr>
          ))}
          <tr>
            <td style={{ padding: '10px 12px', fontWeight: 700, fontSize: 13, color: ui.text }}>
              Total {title}
            </td>
            <td
              style={{
                padding: '10px 12px',
                textAlign: 'right',
                fontWeight: 700,
                fontSize: 13,
                color: ui.text,
              }}
            >
              {money(section?.total)}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const IncomeComparativeTable = ({ ui, comparative, showTotal, title }) => {
  const periods = Array.isArray(comparative?.periods) ? comparative.periods : [];
  const rows = (Array.isArray(comparative?.rows) ? comparative.rows : []).filter((row) =>
    INCOME_ROW_KEYS.has(row.key)
  );
  const emphasize = new Set(['gross_profit', 'net_income']);

  if (!periods.length) {
    return (
      <div style={{ border: `1px solid ${ui.border}`, borderRadius: 12, padding: 16, background: ui.panel }}>
        <p style={{ margin: 0, color: ui.muted, fontSize: 13 }}>
          No hay periodos con asientos contabilizados para comparar.
        </p>
      </div>
    );
  }

  return (
    <div
      style={{
        border: `1px solid ${ui.border}`,
        borderRadius: 12,
        overflow: 'hidden',
        background: ui.page || ui.panel,
      }}
    >
      <div
        style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${ui.border}`,
          background: ui.header,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          flexWrap: 'wrap',
          alignItems: 'baseline',
        }}
      >
        <div>
          <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: ui.text }}>
            {title || 'Estado de resultados comparativo'}
          </h4>
          <p style={{ margin: '4px 0 0', fontSize: 12, color: ui.muted }}>
            Cada columna es un periodo. El salto muestra la variación respecto al periodo anterior.
          </p>
        </div>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 720 }}>
          <thead>
            <tr style={{ background: ui.header, borderBottom: `1px solid ${ui.border}` }}>
              <th
                style={{
                  padding: '8px 12px',
                  textAlign: 'left',
                  fontSize: 11,
                  fontWeight: 700,
                  color: ui.muted,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  position: 'sticky',
                  left: 0,
                  background: ui.header,
                  zIndex: 1,
                  minWidth: 160,
                }}
              >
                Concepto
              </th>
              {periods.map((period) => (
                <th
                  key={period.key}
                  style={{
                    padding: '8px 10px',
                    textAlign: 'right',
                    fontSize: 11,
                    fontWeight: 700,
                    color: ui.muted,
                    letterSpacing: '0.03em',
                    textTransform: 'uppercase',
                    whiteSpace: 'nowrap',
                    minWidth: 96,
                  }}
                >
                  {period.label}
                </th>
              ))}
              {showTotal ? (
                <th
                  style={{
                    padding: '8px 12px',
                    textAlign: 'right',
                    fontSize: 11,
                    fontWeight: 700,
                    color: ui.muted,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                    minWidth: 100,
                  }}
                >
                  Total año
                </th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const isEmphasized = emphasize.has(row.key);
              return (
                <tr
                  key={row.key}
                  style={{
                    borderBottom: `1px solid ${ui.border}`,
                    background: isEmphasized
                      ? `color-mix(in srgb, ${ui.interactive} 8%, ${ui.panel})`
                      : 'transparent',
                  }}
                >
                  <td
                    style={{
                      padding: '10px 12px',
                      fontSize: 13,
                      fontWeight: isEmphasized ? 700 : 600,
                      color: ui.text,
                      position: 'sticky',
                      left: 0,
                      background: isEmphasized
                        ? `color-mix(in srgb, ${ui.interactive} 8%, ${ui.panel})`
                        : ui.panel,
                      zIndex: 1,
                    }}
                  >
                    {row.label}
                  </td>
                  {periods.map((period) => {
                    const value = Number(row.values?.[period.key] || 0);
                    const delta = row.deltas?.[period.key];
                    return (
                      <td
                        key={`${row.key}-${period.key}`}
                        style={{
                          padding: '8px 10px',
                          textAlign: 'right',
                          verticalAlign: 'top',
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: isEmphasized ? 700 : 600,
                            color: ui.text,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {moneyCompact(value)}
                        </div>
                        <div style={{ marginTop: 2, display: 'flex', justifyContent: 'flex-end' }}>
                          <DeltaBadge delta={delta} ui={ui} />
                        </div>
                      </td>
                    );
                  })}
                  {showTotal ? (
                    <td
                      style={{
                        padding: '10px 12px',
                        textAlign: 'right',
                        fontSize: 12,
                        fontWeight: 700,
                        color: ui.text,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {row.total != null ? moneyCompact(row.total) : '—'}
                    </td>
                  ) : null}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FinanceStatementsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [tab, setTab] = useState('income');
  const [incomeView, setIncomeView] = useState('comparative');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [data, setData] = useState(null);
  const [comparative, setComparative] = useState(null);
  const [periodFilter, setPeriodFilter] = useState('last_12');
  const [secondaryYear, setSecondaryYear] = useState(String(new Date().getFullYear()));
  const [secondaryMonth, setSecondaryMonth] = useState(String(new Date().getMonth()));
  const [isLoading, setIsLoading] = useState(true);
  const [isCompLoading, setIsCompLoading] = useState(true);
  const [error, setError] = useState('');
  const [compError, setCompError] = useState('');
  const lastCompParamsKeyRef = React.useRef('');
  const latestCompParamsKeyRef = React.useRef('');

  const availableYearsKey = useMemo(() => {
    const years = Array.isArray(comparative?.available_years)
      ? comparative.available_years.map(Number).filter(Boolean)
      : [];
    if (!years.length) return String(new Date().getFullYear());
    return [...new Set(years)].sort((a, b) => a - b).join('|');
  }, [comparative?.available_years]);

  const availableYears = useMemo(() => {
    const years = availableYearsKey
      .split('|')
      .map(Number)
      .filter((year) => Number.isFinite(year) && year > 0);
    return years.length ? years : [new Date().getFullYear()];
  }, [availableYearsKey]);

  const selectedPeriod = useMemo(
    () =>
      resolvePeriodSelection({
        period: periodFilter,
        dateValues: availableYears.flatMap((year) => [
          new Date(year, 0, 1),
          new Date(year, 11, 31),
        ]),
        secondaryYear,
        secondaryMonth,
        titles: {
          monthly: 'Estado de resultados comparativo',
          annual: 'Estado de resultados anual',
          quarterly: 'Estado de resultados trimestral',
          semestral: 'Estado de resultados semestral',
        },
      }),
    [periodFilter, availableYears, secondaryYear, secondaryMonth]
  );

  const comparativeParams = useMemo(() => {
    const columns = selectedPeriod?.columns || [];
    const first = columns[0];
    const last = columns[columns.length - 1];
    const start = first?.start || selectedPeriod?.range?.start;
    const end = last?.end || selectedPeriod?.range?.end;
    if (!start || !end) return null;
    const mode =
      selectedPeriod?.mode === 'year' || selectedPeriod?.mode === 'year_ytd' ? 'year' : 'month';
    const grain = mode === 'year' ? 'annual' : 'monthly';
    return {
      mode,
      grain,
      date_from: toIsoDate(start),
      date_to: toIsoDate(end),
    };
  }, [selectedPeriod]);

  const comparativeParamsKey = useMemo(() => {
    if (!comparativeParams?.date_from || !comparativeParams?.date_to) return '';
    return `${comparativeParams.mode}|${comparativeParams.grain}|${comparativeParams.date_from}|${comparativeParams.date_to}`;
  }, [comparativeParams]);

  latestCompParamsKeyRef.current = comparativeParamsKey;

  const loadDetail = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const payload = await getFinancialStatements(params);
      setData(payload);
    } catch (err) {
      setError(err.message || 'No se pudieron cargar los estados contables.');
      setData(null);
    } finally {
      setIsLoading(false);
    }
  }, [dateFrom, dateTo]);

  const loadComparative = useCallback(
    async (options = {}) => {
      const force = Boolean(options?.force);
      const requestedKey = comparativeParamsKey;
      if (!requestedKey || !comparativeParams) {
        setIsCompLoading(false);
        return;
      }
      // Evita re-fetch infinito cuando available_years cambia la identidad del memo
      // pero el rango pedido es el mismo.
      if (!force && lastCompParamsKeyRef.current === requestedKey) {
        setIsCompLoading(false);
        return;
      }
      try {
        setIsCompLoading(true);
        setCompError('');
        const payload = await getFinancialStatementsComparative(comparativeParams);
        // Descarta respuestas obsoletas si el usuario cambió el período mientras cargaba.
        if (latestCompParamsKeyRef.current !== requestedKey) return;
        lastCompParamsKeyRef.current = requestedKey;
        setComparative(payload);
      } catch (err) {
        if (latestCompParamsKeyRef.current !== requestedKey) return;
        setCompError(err.message || 'No se pudo cargar la comparativa.');
        setComparative(null);
        lastCompParamsKeyRef.current = '';
      } finally {
        if (latestCompParamsKeyRef.current === requestedKey) {
          setIsCompLoading(false);
        }
      }
    },
    [comparativeParams, comparativeParamsKey]
  );

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (tab !== 'income' || incomeView !== 'comparative') return;
    loadComparative();
  }, [tab, incomeView, comparativeParamsKey, loadComparative]);

  // Si el usuario cambia el período, invalidar cache de la última request.
  useEffect(() => {
    lastCompParamsKeyRef.current = '';
  }, [periodFilter, secondaryYear, secondaryMonth]);

  useEffect(() => {
    if (!availableYears.length) return;
    if (periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral') {
      if (secondaryYear !== 'all' && !availableYears.includes(Number(secondaryYear))) {
        setSecondaryYear('all');
      }
      return;
    }
    if (periodFilter === 'monthly' && !availableYears.includes(Number(secondaryYear))) {
      setSecondaryYear(String(availableYears[availableYears.length - 1]));
    }
  }, [availableYears, periodFilter, secondaryYear]);

  const handlePeriodFilterChange = (nextPeriod) => {
    setPeriodFilter(nextPeriod);
    const now = new Date();
    const latestYear = availableYears[availableYears.length - 1] || now.getFullYear();
    if (nextPeriod === 'annual' || nextPeriod === 'annual_ytd' || nextPeriod === 'quarterly' || nextPeriod === 'semestral') {
      setSecondaryYear('all');
      return;
    }
    if (nextPeriod === 'monthly') {
      setSecondaryYear(
        String(availableYears.includes(now.getFullYear()) ? now.getFullYear() : latestYear)
      );
      setSecondaryMonth(String(now.getMonth()));
    }
  };

  const income = data?.income_statement;
  const balance = data?.balance_sheet;
  const cashFlow = data?.cash_flow;

  const summaryCards = useMemo(() => {
    if (tab === 'income' && incomeView === 'comparative') {
      const periods = Array.isArray(comparative?.periods) ? comparative.periods : [];
      const last = periods[periods.length - 1];
      const prev = periods.length > 1 ? periods[periods.length - 2] : null;
      const metrics = last?.metrics || {};
      const prevMetrics = prev?.metrics || {};
      return [
        {
          label: last ? `Ingresos · ${last.label}` : 'Ingresos',
          value: metrics.revenues || 0,
          delta: prev ? Number(metrics.revenues || 0) - Number(prevMetrics.revenues || 0) : null,
        },
        {
          label: last ? `Utilidad bruta · ${last.label}` : 'Utilidad bruta',
          value: metrics.gross_profit || 0,
          delta: prev ? Number(metrics.gross_profit || 0) - Number(prevMetrics.gross_profit || 0) : null,
        },
        {
          label: last ? `Resultado · ${last.label}` : 'Resultado del ejercicio',
          value: metrics.net_income || 0,
          delta: prev ? Number(metrics.net_income || 0) - Number(prevMetrics.net_income || 0) : null,
        },
        {
          label: 'Periodos',
          value: periods.length,
          isCount: true,
          delta: null,
        },
      ];
    }
    if (!data) return [];
    if (tab === 'cashflow') {
      return [
        { label: 'Caja inicial', value: cashFlow?.opening_cash || 0 },
        { label: 'Flujo operativo', value: cashFlow?.operating?.total || 0 },
        { label: 'Variación de caja', value: cashFlow?.net_change || 0 },
        { label: 'Caja final', value: cashFlow?.closing_cash || 0 },
      ];
    }
    if (tab === 'balance') {
      return [
        { label: 'Activos', value: balance?.assets_total || 0 },
        { label: 'Pasivos', value: balance?.liabilities_total || 0 },
        { label: 'Patrimonio', value: balance?.equity_total || 0 },
        { label: 'Cuadre', value: balance?.difference || 0 },
      ];
    }
    return [
      { label: 'Ingresos', value: income?.revenues?.total || 0 },
      { label: 'Utilidad bruta', value: income?.gross_profit || 0 },
      { label: 'Resultado del ejercicio', value: income?.net_income || 0 },
      { label: 'Activos', value: balance?.assets_total || 0 },
    ];
  }, [data, tab, income, balance, cashFlow, incomeView, comparative]);

  const panelStyle = {
    border: `1px solid ${ui.border}`,
    borderRadius: 12,
    background: ui.panel,
  };

  const inputStyle = {
    height: 36,
    padding: '0 10px',
    borderRadius: 8,
    border: `1px solid ${ui.inputBorder || ui.border}`,
    background: ui.inputBg || ui.panel,
    color: ui.inputText || ui.text,
    fontSize: 13,
    boxSizing: 'border-box',
  };

  const showIncomeComparative = tab === 'income' && incomeView === 'comparative';
  const showIncomeDetail = tab === 'income' && incomeView === 'detail';
  const loadingCards = showIncomeComparative ? isCompLoading : isLoading;

  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          Estados financieros
        </Heading>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: ui.muted }}>
          Generados automáticamente desde asientos contabilizados.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {TABS.map((item) => {
          const active = tab === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setTab(item.key)}
              style={{
                height: 34,
                borderRadius: 8,
                border: 'none',
                padding: '0 12px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: active ? 700 : 500,
                background: active ? ui.interactive : ui.panel,
                color: active ? ui.textOnAccent || '#fff' : ui.text,
                boxShadow: active ? 'none' : `inset 0 0 0 1px ${ui.border}`,
              }}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      {tab === 'income' ? (
        <div style={{ display: 'inline-flex', gap: 2, padding: 3, borderRadius: 10, border: `1px solid ${ui.border}`, background: ui.section || ui.header, width: 'fit-content' }}>
          {INCOME_VIEW_MODES.map((mode) => {
            const active = incomeView === mode.key;
            return (
              <button
                key={mode.key}
                type="button"
                onClick={() => setIncomeView(mode.key)}
                style={{
                  height: 30,
                  border: 'none',
                  borderRadius: 8,
                  padding: '0 12px',
                  cursor: 'pointer',
                  fontSize: 12,
                  fontWeight: 700,
                  background: active ? ui.interactive : 'transparent',
                  color: active ? ui.textOnAccent || '#fff' : ui.muted,
                }}
              >
                {mode.label}
              </button>
            );
          })}
        </div>
      ) : null}

      {showIncomeComparative ? (
        <div
          style={{
            ...panelStyle,
            padding: 12,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'flex-end',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
              <span style={periodLabelStyle(ui)}>Período</span>
              <select
                value={periodFilter}
                onChange={(event) => handlePeriodFilterChange(event.target.value)}
                style={{ ...periodSelectStyle(ui), minWidth: 180, height: 36, fontSize: 13 }}
              >
                {PERIOD_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
            {periodFilter === 'annual' || periodFilter === 'annual_ytd' || periodFilter === 'quarterly' || periodFilter === 'semestral' ? (
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
                <span style={periodLabelStyle(ui)}>Año</span>
                <select
                  value={secondaryYear}
                  onChange={(event) => setSecondaryYear(event.target.value)}
                  style={{ ...periodSelectStyle(ui), minWidth: 110, height: 36, fontSize: 13 }}
                >
                  <option value="all">Todas</option>
                  {availableYears.map((year) => (
                    <option key={year} value={String(year)}>
                      {year}
                    </option>
                  ))}
                </select>
              </label>
            ) : null}
            {periodFilter === 'monthly' ? (
              <>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
                  <span style={periodLabelStyle(ui)}>Año</span>
                  <select
                    value={String(
                      secondaryYear === 'all'
                        ? availableYears[availableYears.length - 1] || new Date().getFullYear()
                        : secondaryYear
                    )}
                    onChange={(event) => setSecondaryYear(event.target.value)}
                    style={{ ...periodSelectStyle(ui), minWidth: 100, height: 36, fontSize: 13 }}
                  >
                    {availableYears.map((year) => (
                      <option key={year} value={String(year)}>
                        {year}
                      </option>
                    ))}
                  </select>
                </label>
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4, flex: '0 0 auto' }}>
                  <span style={periodLabelStyle(ui)}>Mes</span>
                  <select
                    value={secondaryMonth}
                    onChange={(event) => setSecondaryMonth(event.target.value)}
                    style={{ ...periodSelectStyle(ui), minWidth: 100, height: 36, fontSize: 13 }}
                  >
                    {MONTH_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => loadComparative({ force: true })}
            style={{
              height: 36,
              borderRadius: 8,
              border: 'none',
              background: ui.interactive,
              color: ui.textOnAccent || '#fff',
              padding: '0 14px',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <RefreshCw size={14} /> Actualizar
          </button>
        </div>
      ) : (
        <div
          style={{
            ...panelStyle,
            padding: 12,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            alignItems: 'flex-end',
          }}
        >
          <label style={{ display: 'grid', gap: 6, fontSize: 11, color: ui.muted, fontWeight: 600 }}>
            DESDE
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={inputStyle} />
          </label>
          <label style={{ display: 'grid', gap: 6, fontSize: 11, color: ui.muted, fontWeight: 600 }}>
            HASTA
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={inputStyle} />
          </label>
          <button
            type="button"
            onClick={loadDetail}
            style={{
              height: 36,
              borderRadius: 8,
              border: 'none',
              background: ui.interactive,
              color: ui.textOnAccent || '#fff',
              padding: '0 14px',
              fontWeight: 600,
              fontSize: 13,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <RefreshCw size={14} /> Actualizar
          </button>
          {(dateFrom || dateTo) && (
            <button
              type="button"
              onClick={() => {
                setDateFrom('');
                setDateTo('');
              }}
              style={{
                height: 36,
                borderRadius: 8,
                border: `1px solid ${ui.border}`,
                background: 'transparent',
                color: ui.text,
                padding: '0 12px',
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Limpiar periodo
            </button>
          )}
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 10 }}>
        {summaryCards.map((card) => (
          <div key={card.label} style={{ ...panelStyle, padding: 12 }}>
            <p style={{ margin: 0, fontSize: 11, color: ui.muted }}>{card.label}</p>
            <p style={{ margin: '4px 0 0', fontSize: 16, fontWeight: 700, color: ui.text }}>
              {loadingCards ? '…' : card.isCount ? card.value : money(card.value)}
            </p>
            {card.delta != null ? (
              <div style={{ marginTop: 4 }}>
                <DeltaBadge delta={card.delta} ui={ui} />
              </div>
            ) : null}
          </div>
        ))}
      </div>

      {showIncomeComparative ? (
        <>
          {compError ? <p style={{ margin: 0, color: ui.danger, fontSize: 13 }}>{compError}</p> : null}
          {isCompLoading ? (
            <p style={{ margin: 0, color: ui.muted, fontSize: 13 }}>Calculando comparativa…</p>
          ) : null}
          {!isCompLoading && comparative ? (
            <IncomeComparativeTable
              ui={ui}
              comparative={comparative}
              showTotal={selectedPeriod?.mode !== 'year'}
              title={selectedPeriod?.title}
            />
          ) : null}
        </>
      ) : null}

      {error && !showIncomeComparative ? (
        <p style={{ margin: 0, color: ui.danger, fontSize: 13 }}>{error}</p>
      ) : null}
      {isLoading && !showIncomeComparative ? (
        <p style={{ margin: 0, color: ui.muted, fontSize: 13 }}>Calculando estados…</p>
      ) : null}

      {!isLoading && data && showIncomeDetail ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <StatementSectionTable ui={ui} title="Ingresos" section={income?.revenues} />
          <StatementSectionTable ui={ui} title="Costos" section={income?.costs} emptyLabel="Sin costos en el periodo" />
          <div style={{ ...panelStyle, padding: 12, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, color: ui.text }}>Utilidad bruta</span>
            <span style={{ fontWeight: 700, color: ui.text }}>{money(income?.gross_profit)}</span>
          </div>
          <StatementSectionTable ui={ui} title="Gastos" section={income?.expenses} emptyLabel="Sin gastos en el periodo" />
          <div style={{ ...panelStyle, padding: 14, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, fontSize: 15, color: ui.text }}>Resultado del ejercicio</span>
            <span
              style={{
                fontWeight: 800,
                fontSize: 15,
                color: Number(income?.net_income || 0) >= 0 ? '#166534' : ui.danger || '#b91c1c',
              }}
            >
              {money(income?.net_income)}
            </span>
          </div>
        </div>
      ) : null}

      {!isLoading && data && tab === 'balance' ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <StatementSectionTable ui={ui} title="Activos" section={balance?.assets} />
          <StatementSectionTable ui={ui} title="Pasivos" section={balance?.liabilities} />
          <StatementSectionTable ui={ui} title="Patrimonio" section={balance?.equity} />
          <div style={{ ...panelStyle, padding: 14, display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, color: ui.text }}>Activo = Pasivo + Patrimonio</span>
            <span
              style={{
                fontWeight: 700,
                color: Math.abs(Number(balance?.difference || 0)) < 1 ? '#166534' : ui.danger || '#b91c1c',
              }}
            >
              Diferencia {money(balance?.difference)}
            </span>
          </div>
        </div>
      ) : null}

      {!isLoading && data && tab === 'cashflow' ? (
        <div style={{ display: 'grid', gap: 12 }}>
          <CashFlowBlock ui={ui} title="Operación" section={cashFlow?.operating} />
          <CashFlowBlock ui={ui} title="Inversión" section={cashFlow?.investing} />
          <CashFlowBlock ui={ui} title="Financiamiento" section={cashFlow?.financing} />
          <div style={{ ...panelStyle, padding: 14, display: 'grid', gap: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: ui.muted, fontSize: 13 }}>Caja inicial</span>
              <span style={{ fontWeight: 600, color: ui.text }}>{money(cashFlow?.opening_cash)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: ui.muted, fontSize: 13 }}>Variación neta</span>
              <span style={{ fontWeight: 600, color: ui.text }}>{money(cashFlow?.net_change)}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontWeight: 700, fontSize: 15, color: ui.text }}>Caja final</span>
              <span style={{ fontWeight: 800, fontSize: 15, color: ui.text }}>{money(cashFlow?.closing_cash)}</span>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default FinanceStatementsWorkspace;
